import axios from 'axios';
import merge from 'lodash/merge';
import get from 'lodash/get';
import has from 'lodash/has';
import uniqBy from 'lodash/uniqBy';
import * as log from 'loglevel';

import { CRUDStoreBuilder } from '@/store/StoreBuilder';
import { getUrl } from '@/config/api.routes';
import { fileNameParser } from '@/libs';
import { DOCUMENTS } from '@/constants/analytics';
import { buildEncodedUrl } from '@/libs/urlencoding';

const routingAlias = 'management.documents';
const documentsCrud = new CRUDStoreBuilder(routingAlias, 'document', [], 'data.models', true).createStore();

export default merge(documentsCrud, {
  namespaced: true,

  state: {
    documents: [],
    selected: null,
    duplicates: [],
  },

  getters: {
    documentModel: () => ({
      file: {
        payload: {
          url: '',
        },
      },
      description: '',
    }),
  },

  mutations: {
    SELECT_DOCUMENT(state, doc) {
      state.selected = doc;
      return doc;
    },
    // TODO (Gabe) Need to check for pagination
    SET_DOCUMENTS(state, documents) {
      state.documents = documents;
    },

    SET_SEARCHED_DOCUMENTS(state, data) {
      // TODO (Gabe) set in own documentSearchResults array
      let items;

      if (data.isNew) {
        items = data.items;
      } else {
        items = uniqBy(state.documents.concat(data.items), 'id');
      }
      state.documents = items;
    },
    SET_TOTAL_DOCUMENTS(state, rowCount) {
      state.totalDocuments = rowCount;
    },
    SET_DOCUMENT_DUPLICATES(state, duplicates) {
      state.duplicates = duplicates;
    },
    CLEAR_STATE(state) {
      state.documents = [];
      state.totalDocuments = 0;
    },
  },

  actions: {
    appendDocument({ rootState, dispatch }, { document, source }) {
      let file = null;
      // Url documents also have a file object, but we want to use the meta.url instead of the file.payload.url
      if (get(document, 'meta.object_type') === 'url') {
        file = {
          url: get(document, 'meta.url'),
          name: get(document, 'name'),
          mime: get(document, 'meta.mime', 'text/html'),
        };
      } else if (has(document, 'file.payload')) {
        // Typical KB payload
        file = get(document, 'file.payload');
        file.name = get(document, 'name') || get(document, 'file.payload.name');
      } else if (has(document, 'payload._source')) {
        // KB suggestion
        file = get(document, 'payload._source');
      } else {
        // Unexpected
        log.warn(`Unexpected document is added ${JSON.stringify(document)}`);
        return;
      }

      dispatch(
        'notifications/emitEventToSocket',
        { name: DOCUMENTS.ATTACHED,
          payload: {
            agent: get(rootState, 'agent.profile'),
            document: {
              name: file.name,
              url: file.url,
            },
            source,
          },
        },
        { root: true },
      );

      return dispatch('files/attachFile', { file, source }, { root: true });
    },
    async getDocumentListV2({ commit }, params) {
      const [param, searchOffset] = params;

      const url = buildEncodedUrl(
        getUrl('management.documentsV2'),
        param,
      );

      const ret = await axios.get(url);

      ret.data.models = ret.data.models.map(x => {
        x.is_smart_control_enabled = !!get(x, 'meta.smart_control', false);
        return x;
      });
      if (!searchOffset) {
        //append to previous list
        commit('SET_DOCUMENTS', [...this.state.documents.documents, ...ret.data.models]);
      } else {
        commit('SET_DOCUMENTS', ret.data.models);
      }
      return ret;
    },

    async getDocumentV2(_, params) {
      const [id, teams] = params;
      return axios.get(getUrl('management.documentsV2', id), { params: { teams } });
    },
    async getDocument(_, params) {
      const [id, teams] = params;
      return axios.get(getUrl('management.documents', id), { params: { teams } });
    },
    async getDocumentSearchList({ rootState, dispatch }, params) {
      const ret = await axios.get(getUrl('management.documentSearch'), {
        params,
      });
      dispatch(
        'notifications/emitEventToSocket',
        { name: DOCUMENTS.SEARCHED,
          payload: {
            agent: get(rootState, 'agent.profile'),
            query: get(params, 'query'),
          },
        },
        { root: true },
      );
      return ret.data;
    },
    loadConversationDocuments({ commit }, documents) {
      commit('SET_DOCUMENTS', documents);
    },
    selectDocument({ commit }, rawDocument) {
      const filename = get(rawDocument, 'file.payload.filename', '');
      const url = get(rawDocument, 'file.payload.url', '');
      const acl = get(rawDocument, 'file.payload.acl', 'private');
      const genai = get(rawDocument, 'meta.genai', false);
      const crawl = get(rawDocument, 'meta.genai_crawl', false);
      const document = {
        id: rawDocument.id,
        title: rawDocument.name,
        description: rawDocument.description,
        fileExt: fileNameParser(filename)[1],
        teams: rawDocument.teams,
        url,
        acl,
        genai,
        crawl,
      };

      commit('SELECT_DOCUMENT', document);
      return document;
    },
    createDocument({ commit }, params = []) {
      const [item, dataKey = this.dataKey] = params;
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      if (this.pending) {
        commit('PUT_DOCUMENT');
      }

      return axios.post(getUrl('management.documents'), item.formData, config)
        .then(data => {
          // TODO (Gabe) set the right dataKey
          commit('PUT_DOCUMENT_SUCCESS', get(data, dataKey));
          return data;
        });
    },
    updateDocument({ commit }, params = []) {
      const [item, dataKey = this.dataKey] = params;
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      if (this.pending) {
        commit('PUT_DOCUMENT');
      }

      return axios.put(getUrl('management.documents', item.id), item.formData, config)
        .then(data => {
          commit('PUT_DOCUMENT_SUCCESS', get(data, dataKey));
          return data;
        });
    },


    async updateDocumentV2(_, params) {
      const { id, payload } = params;
      return axios.put(getUrl('management.documentsV2', id), payload);
    },

    async createDocumentV2(_, params) {
      return axios.post(getUrl('management.documentsV2'), params);
    },

    async deleteDocumentV2(_, id) {
      return axios.delete(getUrl('management.documentsV2', id));
    },

    async setCrawlCredentials(_, { id, username, password }) {
      return axios.put(`${getUrl('management.documents', id)}/crawl/credentials`, { username, password });
    },

    async getDocumentDuplicates({ commit }, payload) {
      const [params, isFirstPage] = payload;

      const url = buildEncodedUrl(
        `${getUrl('management.documents', 'all')}/duplicates`,
        params,
      );

      const response = await axios.get(url);
      const models = get(response, 'data.models', []);

      if (isFirstPage) {
        commit('SET_DOCUMENT_DUPLICATES', models);
      } else {
        // append to previous list
        commit('SET_DOCUMENT_DUPLICATES', [...this.state.documents.duplicates, ...models]);
      }
      return response;
    },

    async deleteDocumentDuplicate(_, { documentId, itemId }) {
      return axios.delete(`${getUrl('management.documents', documentId)}/duplicates/${itemId}`);
    },
  },
});
